import React, { useEffect, useState } from 'react';
import withRouter from '../../../components/withRouter';
import TableContainer from '../../../components/TableContainerReactTable';
import AddEditBlockedKeywordsModal from '../components/blockedkeywords/AddEditBlockedKeywordsModal';
import { getBlockedKeywords } from '../../../api/blockedKeywordsApi';
import { BlockedKeywords } from '../../../types/blocked-keywords';
import { useScopes } from '../../../hooks/useScopes';
import { ApiScope } from '../../../types/api-scopes';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Pencil, Plus } from 'lucide-react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

const Index = (props: any) => {
    const { userProfile, hasScope } = useScopes();
    const [data, setData] = useState<BlockedKeywords[]>([]);
    const [dataToEdit, setDataToEdit] = useState<BlockedKeywords | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);

    useEffect(() => {
        getBlockedKeywords().then((res) => {
            setData(res);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    const toggleAddEditModal = () => {
        setIsAddModalOpen(!isAddModalOpen);
    };

    const handleUpdateData = (newData: BlockedKeywords) => {
        const updatedData = data.map((item) => {
            if (item.id === newData.id) {
                return newData;
            }
            return item;
        });
        setData(updatedData);
    };

    const handleAddData = (newData: BlockedKeywords) => {
        setData([...data, newData]);
    };

    const dateFormatOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        timeZoneName: 'shortOffset',
    };

    const columns = [
        {
            header: '#',
            cell: (cell: any) => {
                return (
                    <span className="font-semibold">{cell.getValue()}</span>
                );
            },
            accessorKey: 'id',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Nome',
            accessorKey: 'name',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Palavras',
            accessorFn: (row: any) => {
                return row.words.split('\n').length;
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Campanhas',
            accessorKey: 'campaigns.length',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Criado em',
            accessorFn: (row: any) => {
                const date = new Date(row.createdAt);
                return date.toLocaleDateString('pt-BR', dateFormatOptions);
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Atualizado em',
            accessorFn: (row: any) => {
                const date = new Date(row.updatedAt);
                return date.toLocaleDateString('pt-BR', dateFormatOptions);
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Actions',
            cell: (cell: any) => {
                const row = cell.row.original;
                let actionCell = [];

                // Botão de editar
                if (hasScope(ApiScope.CAMPAIGN_WRITE)) {
                    actionCell.push(<TooltipProvider key="action_edit">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 text-primary hover:bg-primary/10 h-9 px-4 py-2"
                                        id={'action_edit_' + row.id}
                                        onClick={() => {
                                            setDataToEdit(row);
                                            toggleAddEditModal();
                                        }}
                                ><Pencil className="h-4 w-4" /></button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Editar</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>);
                }

                return <div className="flex gap-2">{actionCell}</div>;
            },
            enableColumnFilter: false,
        },
    ];

    document.title = 'Palavras Chave Proteções | Ad Pulse';
    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <Card>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <CardTitle>Listas de palavras chave</CardTitle>
                                        {hasScope(ApiScope.CAMPAIGN_WRITE) && (
                                            <Button
                                                onClick={() => {
                                                    setDataToEdit(null);
                                                    toggleAddEditModal();
                                                }}
                                                className="flex items-center gap-2"
                                            >
                                                <Plus className="h-4 w-4" />
                                                Criar lista
                                            </Button>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {!loading ? <TableContainer
                                            columns={(columns || [])}
                                            data={(data || [])}
                                            isGlobalFilter={true}
                                            customPageSize={30}
                                            SearchPlaceholder="Procurar..."
                                        /> :
                                        <div className="text-center">
                                            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                                        </div>}

                                    {hasScope(ApiScope.CAMPAIGN_WRITE) && (
                                        <AddEditBlockedKeywordsModal
                                            blockedKeywords={dataToEdit}
                                            isOpen={isAddModalOpen}
                                            toggle={toggleAddEditModal}
                                            onAddData={handleAddData}
                                            onUpdateData={handleUpdateData}
                                        />
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default withRouter(Index);
